import React, { useMemo, useState } from 'react';
import StatTile from '../StatTile';
import { formatNumber, PALETTE } from '../charts';
import { Badge, Icon, Pagination, ProgressBar, SearchInput, Tabs } from '../ui';
import { SeriesCard } from './MetricCard';
import { duration } from './units';

// The two node pools and who scales them. This split is deliberate and easy to
// misread: Cluster Autoscaler keeps the general pool (which runs Karpenter itself,
// so Karpenter cannot own it), and Karpenter owns the agent pool from zero.
function PoolCard({ pool }) {
  return (
    <div className="card dashboard-card dashboard-tile-6">
      <div className="tile-head">
        <div className="tile-head-text">
          <h3 className="tile-title"><span className="mono">{pool.name}</span></h3>
          <div className="tile-subtitle">{pool.purpose}</div>
        </div>
        <Badge tone={pool.manager === 'Karpenter' ? 'violet' : 'accent'}>{pool.manager}</Badge>
      </div>
      <dl className="kv">
        <dt>Nodes now</dt>
        <dd className="num fw-600">{pool.nodes}</dd>
        <dt>Holding</dt>
        <dd className="num">
          {formatNumber(pool.agents || 0)} {pool.agents === 1 ? 'agent' : 'agents'},{' '}
          {formatNumber(pool.balloons || 0)} {pool.balloons === 1 ? 'balloon' : 'balloons'},{' '}
          {formatNumber(Math.max(0, (pool.pods || 0) - (pool.agents || 0) - (pool.balloons || 0)))} system pods
        </dd>
        <dt>Bounds</dt>
        <dd>
          {pool.cpu_limit
            ? `from ${pool.min ?? 0}, capped at ${pool.cpu_limit} vCPU by the NodePool`
            : pool.min != null ? `${pool.min} to ${pool.max} nodes` : 'not readable: the node group could not be described'}
        </dd>
        <dt>Instance types</dt>
        <dd className="row row-wrap gap-1">
          {pool.instance_types.map(t => <Badge key={t} tone="outline" mono>{t}</Badge>)}
        </dd>
        <dt>Capacity</dt>
        <dd className="row row-wrap gap-1">
          {(pool.capacity_types || []).map(t => <Badge key={t} tone="outline">{t}</Badge>)}
        </dd>
      </dl>
    </div>
  );
}

// Sized for a cluster of a couple of hundred nodes: one page of rows at a time, a pool
// filter and a search, and every numeric column sortable, so the busy or the stuck nodes
// can be brought to the top instead of scrolled to.
const NODE_PAGE = 25;
const NODE_COLUMNS = [
  { key: 'name', label: 'Node' },
  { key: 'pool', label: 'Pool' },
  { key: 'instance_type', label: 'Type' },
  { key: 'zone', label: 'Zone' },
  { key: 'age_s', label: 'Age', numeric: true },
  { key: 'agents', label: 'Agents', numeric: true, align: 'right',
    help: 'Agent pods on the node. Only the gVisor pool runs them.' },
  { key: 'balloons', label: 'Balloons', numeric: true, align: 'right',
    help: 'Placeholder pods keeping spare room warm for the next wakes; a waking agent pushes one out.' },
  { key: 'pods', label: 'All pods', numeric: true, align: 'right',
    help: 'Every pod on the node: agents, balloons, the per-node daemons (network, EFS, logs) and the cluster\u2019s own services.' },
  { key: 'cpu_req_pct', label: 'CPU requested', numeric: true },
  { key: 'mem_req_pct', label: 'Memory requested', numeric: true },
];

function sortNodes(nodes, key, dir) {
  const col = NODE_COLUMNS.find(c => c.key === key) || NODE_COLUMNS[0];
  const sign = dir === 'desc' ? -1 : 1;
  return [...nodes].sort((a, b) => {
    const x = a[col.key];
    const y = b[col.key];
    let d;
    if (col.numeric) d = (x ?? -1) - (y ?? -1);
    else d = String(x ?? '').localeCompare(String(y ?? ''));
    return d !== 0 ? d * sign : String(a.name).localeCompare(String(b.name));
  });
}

function SortHeader({ col, sort, onSort }) {
  const active = sort.key === col.key;
  const next = active && sort.dir === 'asc' ? 'desc' : active ? 'asc' : (col.numeric ? 'desc' : 'asc');
  return (
    <th scope="col" className={col.align === 'right' ? 'text-right' : undefined}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        title={col.help}>
      <button type="button" className="th-sort" onClick={() => onSort({ key: col.key, dir: next })}>
        {col.label}
        {active && <Icon name={sort.dir === 'asc' ? 'chevronUp' : 'chevronDown'} size={12} />}
      </button>
    </th>
  );
}

function NodeTable({ nodes, pools }) {
  const [pool, setPool] = useState('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'pool', dir: 'asc' });
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = nodes.filter(n => (pool === 'all' || n.pool === pool)
      && (!q || [n.name, n.instance_type, n.zone, n.capacity_type].some(v => String(v || '').toLowerCase().includes(q))));
    return sortNodes(rows, sort.key, sort.dir);
  }, [nodes, pool, query, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / NODE_PAGE));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * NODE_PAGE, current * NODE_PAGE);
  const poolTabs = [{ value: 'all', label: 'All', count: nodes.length },
    ...pools.map(p => ({ value: p.name, label: p.name, count: nodes.filter(n => n.pool === p.name).length }))];
  const reset = fn => (v) => { fn(v); setPage(1); };

  return (
    <>
      <div className="toolbar">
        <SearchInput value={query} onChange={reset(setQuery)} placeholder="Filter by node, type or zone"
                     label="Filter nodes" />
        {pools.length > 1 && (
          <Tabs size="sm" label="Pool" value={pool} onChange={reset(setPool)} tabs={poolTabs} />
        )}
        <div className="toolbar-spacer" />
        <span className="text-sm text-muted num">
          {filtered.length === nodes.length ? `${nodes.length} nodes` : `${filtered.length} of ${nodes.length} nodes`}
        </span>
      </div>
      <div className="table-container">
        <table className="table table-stack">
          <thead>
            <tr>
              {NODE_COLUMNS.map(col => <SortHeader key={col.key} col={col} sort={sort} onSort={reset(setSort)} />)}
            </tr>
          </thead>
          <tbody>
            {visible.map(n => (
              <tr key={n.name}>
                <td data-label="Node">
                  <span className="mono text-xs truncate node-name">{n.name.split('.')[0]}</span>
                  <span className="cell-sub">{n.status}</span>
                </td>
                <td data-label="Pool"><span className="mono text-sm">{n.pool}</span></td>
                <td data-label="Type" className="nowrap">
                  {n.instance_type}
                  <span className="cell-sub">{n.capacity_type}</span>
                </td>
                <td data-label="Zone" className="nowrap">{n.zone}</td>
                <td data-label="Age" className="nowrap num">{n.age_s == null ? '—' : duration(n.age_s)}</td>
                <td data-label="Agents" className={`text-right num${n.agents ? ' fw-600' : ' text-muted'}`}>{n.agents ?? '—'}</td>
                <td data-label="Balloons" className="text-right num text-muted">{n.balloons ?? '—'}</td>
                <td data-label="All pods" className="text-right num">{n.pods}</td>
                <td data-label="CPU requested">
                  <div className="usage-row compact">
                    <ProgressBar value={n.cpu_req_pct} max={100} label={`CPU requested ${Math.round(n.cpu_req_pct)}%`} />
                    <span className="usage-value num">{Math.round(n.cpu_req_pct)}%</span>
                  </div>
                </td>
                <td data-label="Memory requested">
                  <div className="usage-row compact">
                    <ProgressBar value={n.mem_req_pct} max={100} label={`Memory requested ${Math.round(n.mem_req_pct)}%`} />
                    <span className="usage-value num">{Math.round(n.mem_req_pct)}%</span>
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={NODE_COLUMNS.length} className="text-muted text-center">No node matches this filter.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={current} pageSize={NODE_PAGE} total={filtered.length} onPage={setPage} noun="nodes" />
    </>
  );
}

export default function ClusterPanel({ data, range, loading }) {
  const c = data?.current || {};
  const pools = data?.pools || [];
  const managers = new Set(pools.map(p => p.manager)).size;
  const cpuUsedPct = c.karpenter_cpu_limit ? (c.karpenter_cpu_used / c.karpenter_cpu_limit) * 100 : null;
  return (
    <>
      <div className="dashboard-grid">
        <StatTile span={3} icon="server" label="Nodes" value={`${c.nodes_ready ?? '—'}/${c.nodes_total ?? '—'}`}
          sub={`ready · EKS ${c.version || '—'}`} tone={c.nodes_ready < c.nodes_total ? 'negative' : undefined} />
        <StatTile span={3} icon="users" label="Agents running" value={formatNumber(c.agents_running || 0)}
          sub={`${formatNumber(c.agents_hibernated || 0)} hibernated`} />
        <StatTile span={3} icon="clock" label="Agents pending" value={formatNumber(c.agents_pending || 0)}
          sub={c.agents_pending ? 'waiting for a node' : 'none waiting'}
          tone={c.agents_pending ? 'warning' : 'positive'}
          help="Agent pods the scheduler could not place yet. A few for a minute after a burst of wakes is normal: the balloon pods give way first and Karpenter adds a node. Pending that persists means the NodePool limit or an instance shortage." />
        <div className="card dashboard-card kpi-tile dashboard-tile-3">
          <div className="kpi-label"><span>Karpenter capacity</span></div>
          <div className="kpi-value">{c.karpenter_cpu_used ?? '—'}<span className="kpi-unit"> / {c.karpenter_cpu_limit ?? '—'} vCPU</span></div>
          {cpuUsedPct != null && <ProgressBar value={cpuUsedPct} max={100} label="Karpenter vCPU used of the NodePool limit" />}
          <div className="kpi-meta"><span>{c.balloons ?? 0} balloon pods holding warm headroom</span></div>
        </div>
      </div>

      <div className="section-head">
        <h2>Node pools</h2>
        <p>{managers === 2 ? 'two autoscalers, one pool each' : `${pools.length} in use`}</p>
      </div>
      <div className="dashboard-grid">
        {pools.map(p => <PoolCard key={p.name} pool={p} />)}
      </div>

      <div className="section-head">
        <h2>Over time</h2>
      </div>
      <div className="dashboard-grid">
        <SeriesCard
          title="Nodes by pool" subtitle="Ready nodes in each pool" range={range} loading={loading}
          timestamps={data?.timestamps} series={data?.series}
          lines={[
            { key: 'nodes_gvisor', label: 'Agent nodes (gVisor)', color: PALETTE[3] },
            { key: 'nodes_agents', label: 'General nodes', color: PALETTE[0] },
          ]}
        />
        <SeriesCard
          title="Agent pods" subtitle="Running and pending" range={range} loading={loading}
          timestamps={data?.timestamps} series={data?.series}
          lines={[
            { key: 'agents_running', label: 'Running', color: PALETTE[5] },
            { key: 'agents_pending', label: 'Pending', color: PALETTE[2] },
          ]}
          help="Pending spikes line up with the node count stepping up: that is a wake burst being absorbed. A pending line that stays up while nodes do not move is the case to investigate."
        />
        <SeriesCard
          title="Requested capacity" subtitle="Share of allocatable CPU and memory" range={range} loading={loading}
          unit="percent" yMax={100} timestamps={data?.timestamps} series={data?.series}
          lines={[
            { key: 'cpu_requested_pct', label: 'CPU', color: PALETTE[0] },
            { key: 'mem_requested_pct', label: 'Memory', color: PALETTE[1] },
          ]}
          help="What pods have reserved, not what they use. The scheduler places on requests, so this is the number that decides when a new node is needed."
          span={12}
        />
      </div>

      <div className="section-head">
        <h2>Nodes</h2>
        <p>{(data?.nodes || []).length} in the cluster now</p>
      </div>
      <div className="card card-flush mb-4">
        <NodeTable nodes={data?.nodes || []} pools={pools} />
      </div>
    </>
  );
}
